import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import LabelsAPI from '../../api/labels';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { LABEL_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
  },
};

const labelSortValue = label => {
  if (label.display_order === null || label.display_order === undefined) {
    return 999999;
  }

  const displayOrder = Number(label.display_order);
  return Number.isFinite(displayOrder) ? displayOrder : 999999;
};

const sortLabels = labels => {
  return [...labels].sort((labelA, labelB) => {
    const displayOrderDiff = labelSortValue(labelA) - labelSortValue(labelB);
    if (displayOrderDiff !== 0) return displayOrderDiff;

    return labelA.title.localeCompare(labelB.title);
  });
};

export const getters = {
  getLabels(_state) {
    return sortLabels(_state.records);
  },
  getAssignableLabels(_state) {
    return sortLabels(_state.records.filter(record => !record.managed));
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getLabelsOnSidebar(_state) {
    return sortLabels(_state.records.filter(record => record.show_on_sidebar));
  },
  getLabelById: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
};

export const actions = {
  revalidate: async function revalidate({ commit }, { newKey }) {
    try {
      const isExistingKeyValid = await LabelsAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await LabelsAPI.refetchAndCommit(newKey);
        commit(types.SET_LABELS, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },

  get: async function getLabels({ commit }) {
    commit(types.SET_LABEL_UI_FLAG, { isFetching: true });
    try {
      const response = await LabelsAPI.get(true);
      commit(types.SET_LABELS, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createLabels({ commit }, cannedObj) {
    commit(types.SET_LABEL_UI_FLAG, { isCreating: true });
    try {
      const response = await LabelsAPI.create(cannedObj);
      AnalyticsHelper.track(LABEL_EVENTS.CREATE);
      commit(types.ADD_LABEL, response.data);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateLabels({ commit }, { id, ...updateObj }) {
    commit(types.SET_LABEL_UI_FLAG, { isUpdating: true });
    try {
      const response = await LabelsAPI.update(id, updateObj);
      AnalyticsHelper.track(LABEL_EVENTS.UPDATE);
      commit(types.EDIT_LABEL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteLabels({ commit }, payload) {
    commit(types.SET_LABEL_UI_FLAG, { isDeleting: true });
    const id = typeof payload === 'object' ? payload.id : payload;
    const replacementLabelId =
      typeof payload === 'object' ? payload.replacementLabelId : undefined;
    try {
      await LabelsAPI.delete(id, replacementLabelId);
      AnalyticsHelper.track(LABEL_EVENTS.DELETED);
      commit(types.DELETE_LABEL, id);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_LABEL_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_LABEL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_LABELS](_state, data) {
    MutationHelpers.set(_state, sortLabels(data));
  },
  [types.ADD_LABEL](_state, data) {
    MutationHelpers.create(_state, data);
    _state.records = sortLabels(_state.records);
  },
  [types.EDIT_LABEL](_state, data) {
    MutationHelpers.update(_state, data);
    _state.records = sortLabels(_state.records);
  },
  [types.DELETE_LABEL]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
